// The one place a SUPPLIER surface picks which language of a request title to
// show.
//
// ── THE BUG THIS REPLACES ──────────────────────────────────────────────────
// DashboardSupplier had this, three times, on screens only suppliers see:
//
//     request.product?.name_ar || request.product?.name_en
//       || request.product?.name_zh || request.title_ar || request.title_en
//
// Arabic FIRST, unconditionally, regardless of who is reading. A factory in
// Shenzhen with the interface in Chinese got the Arabic title, because Arabic
// happened to be first in a chain nobody had revisited since the product was
// Arabic-only.
//
// The fix is not "strip Arabic". It is "ask who is reading". Since 2026-09-21
// the trilingual columns are filled server-side by the translate-request edge
// function, so title_ar is now a real Arabic TRANSLATION rather than the
// trader's raw text — the problem was never that Arabic existed, it was that
// it was served to people who cannot read it.
//
// ── ORDER ──────────────────────────────────────────────────────────────────
// Viewer's own language, then English, then Chinese, then Arabic. English sits
// second for everyone because it is the lingua franca of this trade and the
// only one of the three that a Chinese and an Arabic reader are both likely to
// make something of. Arabic is LAST rather than absent — see the note below.
//
// ⚠ OPEN QUESTION FOR THE OWNER. The stated rule is "a supplier never sees
// Arabic on any surface". Taken literally that also applies to a supplier
// whose own interface is set to Arabic, who would then be shown English
// instead of the language they chose. This helper serves such a viewer Arabic,
// on the reasoning that the rule's purpose is "never serve text in a script
// the reader cannot read". If the rule is meant absolutely, delete 'ar' from
// ORDER_BY_VIEWER.ar and from the tail of the other two, and the helper
// returns '' instead — the call sites already handle an empty title.

const ORDER_BY_VIEWER = {
  ar: ['title_ar', 'title_en', 'title_zh'],
  en: ['title_en', 'title_zh', 'title_ar'],
  zh: ['title_zh', 'title_en', 'title_ar'],
};

const PRODUCT_ORDER_BY_VIEWER = {
  ar: ['name_ar', 'name_en', 'name_zh'],
  en: ['name_en', 'name_zh', 'name_ar'],
  zh: ['name_zh', 'name_en', 'name_ar'],
};

const firstNonEmpty = (source, keys) => {
  if (!source) return '';
  for (const k of keys) {
    const v = String(source[k] ?? '').trim();
    if (v) return v;
  }
  return '';
};

/**
 * Title of a request as a SUPPLIER should see it.
 *
 * @param {object} request  a row with title_ar / title_en / title_zh, and
 *                          optionally a joined `product`
 * @param {'ar'|'en'|'zh'} lang  the VIEWER's interface language
 * @returns {string} '' when nothing usable exists — callers already guard
 */
export function supplierRequestTitle(request, lang = 'en') {
  const viewer = ORDER_BY_VIEWER[lang] ? lang : 'en';

  // A product-bound request is named by its product, which is the more
  // specific thing the supplier is being asked to quote. Same ordering rule.
  const fromProduct = firstNonEmpty(request?.product, PRODUCT_ORDER_BY_VIEWER[viewer]);
  if (fromProduct) return fromProduct;

  return firstNonEmpty(request, ORDER_BY_VIEWER[viewer]);
}

/**
 * Same rule for a title nested under `requests` (the shape an offers query
 * returns). Kept as its own export rather than a second argument so call sites
 * read as what they are.
 */
export function supplierOfferRequestTitle(offer, lang = 'en') {
  return supplierRequestTitle(offer?.requests, lang);
}
